import { useTranslation } from 'react-i18next'
import Card from '@/components/ui/Card'
import Button from '@/components/ui/Button'
import type { ZakatCalculationResult } from '@/types/zakat'

interface ZakatSummaryProps {
  result: ZakatCalculationResult | null
  onShowHowTo?: () => void
}

export default function ZakatSummary({ result, onShowHowTo }: ZakatSummaryProps) {
  const { t, i18n } = useTranslation()
  const locale = i18n.language === 'ar' ? 'ar-EG' : 'en'

  const formatNumber = (value: number, unit?: string) => {
    if (unit === 'currency') {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: 'EGP',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value)
    }

    const formatted = new Intl.NumberFormat(locale, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(value)

    if (unit === 'gram') {
      return `${formatted} ${t('zakat.unitGram')}`
    } else if (unit === 'kilogram') {
      return `${formatted} ${t('zakat.unitKilogram')}`
    } else {
      return formatted
    }
  }

  const formatZakatAmount = (value: number) => {
    return new Intl.NumberFormat(locale, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(value)
  }

  return (
    <Card className="bg-gradient-to-br from-primary-50 to-primary-100 dark:from-primary-900/20 dark:to-primary-800/20">
      <div className="space-y-6">
        <h3 className="text-xl font-bold text-primary-700 dark:text-primary-300 mb-4">
          {t('zakat.summaryTitle')}
        </h3>

        {result?.nisabUnit !== 'animals' && (
          <div>
            <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              {t('zakat.nisabValue')}
            </h4>
            <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {result && result.nisab > 0 ? formatNumber(result.nisab, result.nisabUnit) : '-'}
              </p>
            </div>
          </div>
        )}

        <div>
          <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
            {t('zakat.dueZakat')}
          </h4>
          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
            {result && result.isZakatDue ? (
              <>
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                  {result.nisabUnit === 'animals' && result.zakatDescription
                    ? result.zakatDescription
                    : formatZakatAmount(result.zakatAmount)}
                </p>
                {result.nisabUnit === 'animals' && result.zakatDescription && (
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                    ({t('zakat.livestockNote')})
                  </p>
                )}
              </>
            ) : (
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">-</p>
            )}
          </div>
        </div>

        {onShowHowTo && (
          <Button
            variant="secondary"
            size="lg"
            className="w-full bg-gray-900 text-white hover:bg-gray-800 dark:bg-gray-700 dark:hover:bg-gray-600"
            onClick={onShowHowTo}
          >
            {t('zakat.howToCalculate')}
          </Button>
        )}
      </div>
    </Card>
  )
}

